/**
 * 捏脸模块的游戏侧接入入口（67 批）。
 * - ensureSprites()：懒加载部件精灵，进开局设定页才 await（主菜单不为 3.1MB webp 买单）。
 * - avatarPngSrc()：AvatarConfig → PNG dataURL，带缓存；列表/聊天等一屏多头像场景的唯一正确姿势。
 *   ⛔ 别把 renderAvatarSVG 的 data-uri 直塞 <img> 批量用：一份内嵌约 14 个精灵 dataURL（~1.3MB 字符串），
 *   30 个 NPC 就是 40MB；SVG 字符串只允许内联进 DOM 单例预览（页内同时只有一份时才无滤镜 id 冲突）。
 * - applyAvatarImg()：异步把渲染图写进 <img>，失败/未就绪时保持占位（emoji）不动。
 */
import { AvatarConfig } from './config';
import { renderAvatarSVG } from './renderer';
import { loadAllSprites } from './sprites';

let spritesPromise: Promise<void> | null = null;

/** 部件精灵就绪（幂等，重复调用复用同一 promise） */
export function ensureSprites(): Promise<void> {
  if (!spritesPromise) spritesPromise = loadAllSprites();
  return spritesPromise;
}

const pngCache = new Map<string, Promise<string>>();

/** 已出好的 PNG（同步可读）：切屏时同一张脸直接就地填充，⛔ 先清空槽再等异步（那会白一帧） */
const pngReady = new Map<string, string>();

/** 已出好的位图：槽里用 <canvas> **同步** drawImage —— 唯一能保证「插进 DOM 的这一帧就有脸」的形态
 *  （<img> 即使 src 已缓存，首次插入仍可能白一帧解码）。切屏白闪的最终收口。 */
const bitmapReady = new Map<string, ImageBitmap>();
/** 位图上限：330×330×4 ≈ 435KB/张，超过就按插入顺序丢最老的（丢了退回 <img>，不影响正确性） */
const BITMAP_CACHE_MAX = 80;

function rememberBitmap(key: string, bmp: ImageBitmap): void {
  bitmapReady.set(key, bmp);
  while (bitmapReady.size > BITMAP_CACHE_MAX) {
    const oldest = bitmapReady.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    bitmapReady.get(oldest)?.close();
    bitmapReady.delete(oldest);
  }
}

const keyOf = (config: AvatarConfig, size: number): string => `${size}|${JSON.stringify(config)}`;

/**
 * 全局唯一出图边长：详情页最大框 110 CSS px × DPR 封顶 3 = 330。
 * 所有头像位一律按此出图、由浏览器降采样到小框——每个 NPC 全游戏只缓存一张，
 * 开局/读档 prefetchAvatars 热一次缓存，切页就不再闪 emoji 兜底。
 */
export const AVATAR_MASTER_PX = 330;

/** AvatarConfig → PNG dataURL（size = 输出边长 px）；同配置同尺寸命中缓存 */
export function avatarPngSrc(config: AvatarConfig, size = AVATAR_MASTER_PX): Promise<string> {
  const key = keyOf(config, size);
  const ready = pngReady.get(key);
  if (ready) return Promise.resolve(ready);
  let p = pngCache.get(key);
  if (!p) {
    p = ensureSprites()
      .then(() => rasterizeCanvas(config, size))
      .then(canvas => {
        const png = canvas.toDataURL('image/png');
        pngReady.set(key, png);
        // 位图一并备好并**纳入 promise**：调用方 await 到它时，canvas 同步绘制路径已可用（失败退回 <img>）
        if (typeof createImageBitmap !== 'function') return png;
        return createImageBitmap(canvas)
          .then(bmp => { rememberBitmap(key, bmp); return png; })
          .catch(() => png);
      });
    pngCache.set(key, p);
  }
  return p;
}

/**
 * SVG → canvas，⛔ 任何缓存（缓存都在调用方）。
 * 捏脸条那种每开一个类目就换新配置批量出图的场景走 `avatar/thumbs.ts`（页内 LRU）。
 */
export function rasterizeCanvas(config: AvatarConfig, size: number): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([renderAvatarSVG(config)], { type: 'image/svg+xml;charset=utf-8' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      URL.revokeObjectURL(url);
      if (!ctx) return reject(new Error('canvas 2d unavailable'));
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('avatar svg rasterize failed'));
    };
    img.src = url;
  });
}

/** SVG → canvas → PNG dataURL，⛔ 任何缓存（对外保持旧签名） */
export function rasterizeAvatar(config: AvatarConfig, size: number): Promise<string> {
  return rasterizeCanvas(config, size).then(canvas => canvas.toDataURL('image/png'));
}

/**
 * 把捏脸渲染图异步写进 <img>（仅当元素还挂在文档上）。失败静默，占位保持原样。
 * 出图恒为 master 尺寸（见 AVATAR_MASTER_PX），size 已作废仅留签名兼容。
 */
export function applyAvatarImg(img: HTMLImageElement, config: AvatarConfig, _size = 96): void {
  avatarPngSrc(config, AVATAR_MASTER_PX)
    .then(src => {
      if (img.isConnected) img.src = src;
    })
    .catch(() => {});
}

/**
 * CSS 框宽 → 出图像素：手机 Retina 按 DPR 放大（封顶 3× 控缓存内存）。
 * ⛔ 头像位现在一律走 master 出图（fillAvatarSlot/applyAvatarImg），本函数仅供
 *    仍按框宽单独出图的旧调用点使用；新代码优先用 prefetchAvatar / fillAvatarSlot。
 */
export function displayPx(cssPx: number): number {
  const dpr = Math.min(Math.max(globalThis.devicePixelRatio || 1, 1), 3);
  return Math.round(cssPx * dpr);
}

/** 捏脸配置是否值得渲染成图（旧档/固定 NPC 无此字段时走 emoji 兜底） */
export function hasAvatarRender(config: AvatarConfig | undefined | null): boolean {
  return !!config && typeof config.face === 'number';
}

/** 预热：把一张脸按 master 尺寸渲进缓存（fire-and-forget，供开局/读档批量调用） */
export function prefetchAvatar(config: AvatarConfig | undefined | null): void {
  if (!hasAvatarRender(config)) return;
  void avatarPngSrc(config as AvatarConfig, AVATAR_MASTER_PX).catch(() => {});
}

/**
 * 空闲期预取部件精灵（3.1MB，low 优先级，2026-10-29 §3.4cm）：
 * 让首次出现头像时 `ensureSprites()` 已解析，`fillAvatarSlot` 不必等网络——否则玩家第一眼看到的是
 * emoji 兜底。幂等，主菜单出现后调一次即可。
 */
export function warmupAvatarParts(): void {
  const start = () => { void ensureSprites().catch(() => { }); };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(start, { timeout: 3000 });
    return;
  }
  setTimeout(start, 1500);
}

/**
 * 头像槽统一入口（2026-10-29 §3.4cm 三轮）：
 *
 * 四条不变量（防「白底闪一下再出头像」）：
 *  ① 同一个槽 + 同一张脸 → 直接 return，⛔ 不重建（切屏时顶栏头像白闪就是重建造成的）。
 *  ② 已有 `ImageBitmap` → 用 `<canvas>` **同步** drawImage 插入，插入那一帧就有脸。
 *  ③ 只有 PNG（位图还没生成）→ 同步填 `<img>`（有预解码，通常同帧）。
 *  ④ 还没出图 → **不清空槽**（保留上一张画面或原兜底），出图后就地替换；真失败才落 emoji。
 *
 * 有捏脸配置时不预先落 emoji；无捏脸配置（旧档/固定 NPC）仍走 emoji。size 已作废仅留签名兼容。
 */
export function fillAvatarSlot(slot: HTMLElement, emojiFallback: string, config?: AvatarConfig | null, _size = 40): void {
  if (!hasAvatarRender(config)) {
    slot.innerHTML = '';
    slot.textContent = emojiFallback;
    return;
  }
  const cfg = config as AvatarConfig;
  const key = keyOf(cfg, AVATAR_MASTER_PX);
  const cur = slot.querySelector('.avatar-img') as HTMLElement | null;
  if (cur && cur.dataset.avatarKey === key) return;   // 同一张脸：槽不动（⛔ 切屏白闪）

  const png = pngReady.get(key);
  if (png) {
    mountAvatar(slot, key, png);
    return;
  }

  avatarPngSrc(cfg, AVATAR_MASTER_PX)
    .then(src => {
      if (!slot.isConnected) return;
      mountAvatar(slot, key, src);
    })
    .catch(() => {
      // 出图失败才露出兜底（已在槽里的头像不动）
      if (slot.isConnected && !slot.querySelector('.avatar-img')) {
        slot.innerHTML = '';
        slot.textContent = emojiFallback;
      }
    });
}

/**
 * 把出好的脸装进槽：优先 `<canvas>` 同步绘制（`ImageBitmap` 已在内存，⛔ 任何解码等待），
 * 没有位图才退回 `<img>`。已有同 key 元素就地复用，⛔ 先清空（清空必露底一帧）。
 */
function mountAvatar(slot: HTMLElement, key: string, png: string): void {
  const bmp = bitmapReady.get(key);
  const cur = slot.querySelector('.avatar-img') as HTMLElement | null;
  const tag = cur ? cur.tagName.toLowerCase() : '';

  if (bmp) {
    if (cur && tag === 'canvas' && cur.dataset.avatarKey === key) return;   // 同一张脸已画好
    slot.innerHTML = '';
    const cv = document.createElement('canvas');
    cv.className = 'avatar-img';
    cv.dataset.avatarKey = key;
    cv.width = bmp.width;
    cv.height = bmp.height;
    cv.getContext('2d')?.drawImage(bmp, 0, 0);
    slot.appendChild(cv);
    return;
  }

  const img = cur && tag === 'img' ? (cur as HTMLImageElement) : null;
  if (img) {
    img.dataset.avatarKey = key;
    if (img.src !== png) img.src = png;
    return;
  }
  slot.innerHTML = '';
  const el = document.createElement('img');
  el.className = 'avatar-img';
  el.alt = '';
  el.dataset.avatarKey = key;
  el.src = png;
  slot.appendChild(el);
}
